<template>
  <component
    :is="tag"
    class="lp-button"
    :class="`lp-button--${size}`"
    :style="rootStyle"
    :href="href || null"
    :type="href ? null : type"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <slot></slot>
  </component>
</template>

<script>
export default {
  name: 'lp-button',
  inheritAttrs: false,
  props: {
    // When set the button renders as an <a>; otherwise a native <button>.
    href: {
      type: String,
      default: null,
    },
    // Background — a theme var (e.g. var(--brand)) or a raw color (rgba(...)).
    color: {
      type: String,
      default: 'var(--brand)',
    },
    // Foreground / icon color (svg uses currentColor).
    textColor: {
      type: String,
      default: '#fff',
    },
    // Corner radius — a theme var (var(--radius-md)) or a raw value (999px).
    radius: {
      type: String,
      default: 'var(--radius-md)',
    },
    // Optional border shorthand, e.g. "0.5px solid rgba(255,255,255,0.18)".
    border: {
      type: String,
      default: null,
    },
    // sm = 38px, md = 52px, lg = 60px (height).
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value),
    },
    // Native button type, ignored when rendered as a link.
    type: {
      type: String,
      default: 'button',
    },
    // Stretch to the full width of the container.
    block: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tag() {
      return this.href ? 'a' : 'button';
    },
    rootStyle() {
      return {
        background: this.color,
        color: this.textColor,
        borderRadius: this.radius,
        border: this.border,
        width: this.block ? '100%' : null,
      };
    },
  },
};
</script>

<style lang="scss">
/* ============ BUTTON ============ */
/* Shared CTA look (see .register-cta). Background / text color / radius come
   from props as inline styles so any theme var or raw value can be passed. */
.lp-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  border: none;
  font-family: inherit;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  box-shadow: var(--shadow-sm, 0 4px 14px rgba(0, 0, 0, 0.10));
  transition: opacity 0.15s, box-shadow 0.15s, transform 0.05s;
}
.lp-button:hover { opacity: 0.92; box-shadow: var(--shadow-md, 0 8px 22px rgba(0, 0, 0, 0.14)); }
.lp-button:active { transform: scale(0.99); }

.lp-button--sm {
  height: 38px;
  padding: 0 18px;
  font-size: var(--fs-small);
}
.lp-button--md {
  height: 52px;
  padding: 0 24px;
  font-size: var(--fs-body-lg);
}
.lp-button--lg {
  height: 60px;
  padding: 0 32px;
  gap: 8px;
  font-size: var(--fs-lead);
}
</style>
